<template>
  <div class="contact-page">

    <!-- ── Page Header ── -->
    <section class="page-header">
      <h1 class="page-title">Let's Connect</h1>
      <p class="page-subtitle">Have a bold idea brewing? I'm all ears let's make it real.</p>
    </section>

    <!-- ── Main Content ── -->
    <section class="contact-main">
      <div class="contact-inner">

        <!-- Left: Info -->
        <div class="contact-info">
          <h2 class="info-heading">
            Have a bold idea brewing?<br />
            I'm all ears let's make it real.
          </h2>
          <p class="info-desc">
            Whether it's a research collaboration, speaking engagement,
            workshop, or mentorship opportunity—I'd love to hear from you.
            Let's create something meaningful together.
          </p>

          <div class="info-email">
            <div class="email-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <div class="email-label">Email</div>
              <a href="mailto:bachateravi@gmail.com" class="email-value">bachateravi@gmail.com</a>
            </div>
          </div>

          <div class="social-block">
            <h3 class="social-heading">Social Media</h3>
            <div class="social-icons">
              <a href="https://www.linkedin.com/in/ravindra-bachate-ph-d-715388179/" target="_blank" class="social-btn" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
              </a>
              <a href="https://twitter.com/ravindrabachate" target="_blank" class="social-btn" aria-label="Twitter">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                </svg>
              </a>
              <a href="https://github.com/ravindrabachate" target="_blank" class="social-btn" aria-label="GitHub">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Right: Form -->
        <div class="contact-form-card">
          <h3 class="form-heading">Let's Talk</h3>
          <form @submit.prevent="submitForm" class="contact-form">
            <div class="form-field">
              <label for="name">Your Name</label>
              <input
                type="text"
                id="name"
                v-model="name"
                placeholder="e.g., Ravindra Bachate"
                required
              />
            </div>
            <div class="form-field">
              <label for="email">Email Address</label>
              <input
                type="email"
                id="email"
                v-model="email"
                placeholder="e.g., you@example.com"
                required
              />
            </div>
            <div class="form-field">
              <label for="mobile">Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                v-model="mobile"
                placeholder="e.g., 9876543210"
              />
            </div>
            <div class="form-field">
              <label for="message">Message</label>
              <textarea
                id="message"
                v-model="message"
                placeholder="Tell us briefly about your query or request"
                required
              ></textarea>
            </div>
            <div v-if="successMsg" class="form-success">{{ successMsg }}</div>
            <div v-if="errorMsg" class="form-error">{{ errorMsg }}</div>

            <button type="submit" class="submit-btn" :disabled="sending">
              <svg v-if="!sending" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:8px">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              <span v-if="sending" class="btn-spinner"></span>
              {{ sending ? 'Sending…' : "Let's Talk" }}
            </button>
          </form>
        </div>

      </div>
    </section>

    <!-- ── Collaboration Section ── -->
    <section class="collab-section">
      <h2 class="collab-title">Open to Collaboration</h2>
      <div class="collab-grid">
        <div class="collab-card">
          <h4>Research Collaborations</h4>
          <p>Joint research projects in HCI, AI, and UX</p>
        </div>
        <div class="collab-card">
          <h4>Speaking Engagements</h4>
          <p>Keynotes, talks, and panel discussions</p>
        </div>
        <div class="collab-card">
          <h4>Workshops &amp; Training</h4>
          <p>UX workshops and design thinking sessions</p>
        </div>
        <div class="collab-card">
          <h4>Mentorship</h4>
          <p>1:1 mentoring and career guidance</p>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import emailjs from '@emailjs/browser';

// ─────────────────────────────────────────────────────────────
// EmailJS credentials – fill these in after completing setup:
//   1. Sign up at https://www.emailjs.com
//   2. Add service: Connect your Gmail (bachateravi@gmail.com)
//      → copy the Service ID below
//   3. Create a template with these variables:
//        {{user_name}}, {{user_email}}, {{user_mobile}}, {{message}}
//      → copy the Template ID below
//   4. Account → API Keys → copy your Public Key below
// ─────────────────────────────────────────────────────────────

const EMAILJS_SERVICE_ID  = 'service_6zc970z';   // ← your Service ID
const EMAILJS_TEMPLATE_ID = 'template_3qse1q5';  // ← your Template ID
const EMAILJS_PUBLIC_KEY  = 'o-8KhjXLpQOVzUH2C';  // ← your Public Key

export default {
  name: 'ContactPage',
  data() {
    return {
      name: '',
      email: '',
      mobile: '',
      message: '',
      sending: false,
      successMsg: '',
      errorMsg: '',
    };
  },
  methods: {
    async submitForm() {
      this.sending = true;
      this.successMsg = '';
      this.errorMsg = '';

      const templateParams = {
        user_name:   this.name,
        user_email:  this.email,
        user_mobile: this.mobile || 'Not provided',
        message:     this.message,
      };

      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams,
          EMAILJS_PUBLIC_KEY
        );
        this.successMsg = `Thank you, ${this.name}! Your message has been sent successfully.`;
        this.name = '';
        this.email = '';
        this.mobile = '';
        this.message = '';
      } catch (err) {
        console.error('EmailJS error:', err);
        this.errorMsg = 'Something went wrong. Please try again or email directly at bachateravi@gmail.com';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Page ── */
.contact-page {
  background: var(--color-bg-white);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ── Header ── */
.page-header {
  text-align: center;
  padding: 7rem var(--space-6) var(--space-12);
  background: var(--color-bg-light);
  border-bottom: 1px solid var(--color-bg-subtle);
}
/* .page-title and .page-subtitle come from global style.css */

/* ── Main ── */
.contact-main {
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--section-py) var(--space-6);
}
.contact-inner {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: var(--space-16);
  align-items: start;
}

/* ── Info column ── */
.info-heading {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  color: var(--color-bg-darker);
  line-height: var(--leading-snug);
  margin-bottom: var(--space-4);
}
.info-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-loose);
  margin-bottom: var(--space-8);
}

.info-email {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: var(--space-8);
}
.email-icon {
  width: 42px;
  height: 42px;
  background: var(--color-primary-subtle);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  flex-shrink: 0;
}
.email-label {
  font-size: var(--text-xs);
  color: var(--color-text-300);
  font-weight: var(--font-medium);
  margin-bottom: 0.15rem;
}
.email-value {
  font-size: var(--text-sm);
  color: var(--color-text-700);
  font-weight: var(--font-medium);
  text-decoration: none;
}
.email-value:hover { color: var(--color-primary); }

.social-heading {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: 0.85rem;
}
.social-icons {
  display: flex;
  gap: 0.75rem;
}
.social-btn {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-lg);
  border: 1.5px solid var(--color-border);
  background: var(--color-bg-white);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-700);
  text-decoration: none;
  transition: border-color var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out), background var(--duration-base) var(--ease-out);
}
.social-btn:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: var(--color-primary-subtle);
}

/* ── Form Card ── */
.contact-form-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-md);
}
.form-heading {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: 1.75rem;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.form-field label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-700);
}
.form-field input,
.form-field textarea {
  width: 100%;
  padding: 0.75rem var(--space-4);
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  color: var(--color-bg-darker);
  background: var(--color-bg-white);
  outline: none;
  transition: border-color var(--duration-base) var(--ease-out);
  box-sizing: border-box;
}
.form-field input::placeholder,
.form-field textarea::placeholder {
  color: var(--color-text-300);
}
.form-field input:focus,
.form-field textarea:focus {
  border-color: var(--color-primary);
}
.form-field textarea {
  min-height: 130px;
  resize: vertical;
}

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: var(--color-bg-white);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.9rem var(--space-8);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  cursor: pointer;
  width: 100%;
  transition: background var(--duration-base) var(--ease-out), transform var(--duration-fast) var(--ease-out);
  margin-top: 0.25rem;
}
.submit-btn:hover:not(:disabled) {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
}
.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 8px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.form-success {
  background: var(--color-green-50);
  color: var(--color-green-600);
  border: 1px solid var(--color-green-100);
  border-radius: var(--radius-md);
  padding: 0.75rem var(--space-4);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
}
.form-error {
  background: #fff5f5;
  color: #c53030;
  border: 1px solid #fed7d7;
  border-radius: var(--radius-md);
  padding: 0.75rem var(--space-4);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
}

/* ── Collaboration ── */
.collab-section {
  background: var(--color-bg-light);
  padding: var(--section-py) var(--space-6);
  text-align: center;
  border-top: 1px solid var(--color-bg-subtle);
}
.collab-title {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-10);
}
.collab-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-6);
  max-width: 1000px;
  margin: 0 auto;
}
.collab-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  text-align: left;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.collab-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px);
}
.collab-card h4 {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-2);
}
.collab-card p {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  margin: 0;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .contact-inner { grid-template-columns: 1fr; gap: var(--space-10); }
  .collab-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .contact-main { padding: var(--space-12) var(--space-4); }
  .contact-form-card { padding: 1.75rem var(--space-5); }
  .collab-grid { grid-template-columns: 1fr; }
  .page-header { padding: 6rem var(--space-4) var(--space-10); }
}
</style>
